import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { OutletContextParams } from './delivery-orders-screen';
import {
  CleanLocalDeliveryMethod,
  CleanTransportation,
} from '../../../export-types/cleaned-types';
import { TransportationTabItem } from './transportation-tab-item';
import { DeliveryOrdersTabMethodRow } from './delivery-orders-tab-method-row';
import useSWRInfinite from 'swr/infinite';
import { fetcher, FetcherConfig } from '../../../services/api';
import { useIntersection } from '@mantine/hooks';
import MyLoader from '../../common/loader.component';

type TransportationsTabProps = {
  type?: 'standalone' | 'combined';
  status: 'created' | 'sent' | 'received' | 'packagedCombined';
};

const PAGE_SIZE = 20;

export const TransportationsTabInfiniteScroll = (
  props: TransportationsTabProps,
) => {
  const {
    debouncedInternalId,
    linked,
    attention,
    expanded,
    collapsed,
    onChangeCollapsed,
    selectSupplier,
    direction,
    selectedLocalMethods,
    selectedMethods,
    selectAllOrders,
    onChangeSelectedOrders,
  } = useOutletContext<OutletContextParams>();

  const [collapsedMethods, setCollapsedMethods] = useState<string[]>([]);

  const sort = (() => {
    switch (props.status) {
      case 'created':
        return 'createdAt';
      case 'packagedCombined':
        return 'createdAt';
      case 'sent':
        return 'sentAt';
      case 'received':
        return 'receivedAt';
    }
  })();

  const params = {
    linked,
    attention,
    types: props.type ? [props.type] : undefined,
    status: [props.status],
    name: debouncedInternalId || undefined,
    supplierId: selectSupplier || undefined,
    direction,
    sort,
    localMethodsIds: selectedLocalMethods.map((method) => method.id),
    methodsIds: selectedMethods.map((method) => method.id),
  };

  const getKey = (
    pageIndex: number,
    previousPageData: {
      total: number;
      items: Array<CleanTransportation>;
    },
  ): FetcherConfig | null => {
    if (previousPageData && !previousPageData.items.length) return null;
    return {
      params: { ...params, skip: pageIndex * PAGE_SIZE, take: PAGE_SIZE },
      url: '/transportation',
    };
  };

  const { ref, entry } = useIntersection({
    threshold: 1,
  });

  const {
    data: allData,
    size,
    setSize,
    isLoading: isInfiniteLoading,
  } = useSWRInfinite<{
    total: number;
    items: Array<CleanTransportation>;
  }>(getKey, fetcher);

  const isEmpty = allData?.[0]?.items.length === 0;
  const isLoadingMore =
    isInfiniteLoading ||
    (size > 0 && allData && typeof allData?.[size - 1] === 'undefined');
  const isReachingEnd =
    isEmpty ||
    (allData && allData[allData.length - 1]?.items.length < PAGE_SIZE);

  const lastItem = React.useMemo(() => {
    if (!allData?.length) {
      return undefined;
    }
    const result = allData[allData.length - 1];
    return result.items.length
      ? result.items[result.items.length - 1].id
      : undefined;
  }, [allData]);

  React.useEffect(() => {
    if (entry?.isIntersecting && !isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  }, [entry, isLoadingMore, isReachingEnd, setSize, size]);

  const onToggle = (transportation: CleanTransportation) => {
    if (collapsed.includes(transportation.id)) {
      const newCollapsed = [...collapsed];
      newCollapsed.splice(newCollapsed.indexOf(transportation.id), 1);
      onChangeCollapsed(newCollapsed);
    } else {
      onChangeCollapsed([...collapsed, transportation.id]);
    }
  };

  const onToggleMethod = (storage: string) => {
    if (collapsedMethods.includes(storage)) {
      const newCollapsed = [...collapsedMethods];
      newCollapsed.splice(newCollapsed.indexOf(storage), 1);
      setCollapsedMethods(newCollapsed);
    } else {
      setCollapsedMethods([...collapsedMethods, storage]);
    }
  };

  useEffect(() => {
    if (selectAllOrders) {
      const items = allData?.map((data) => data.items).flat(1) || [];

      onChangeSelectedOrders(
        items.map((order) => order.lines[0].deliveryOrder) || [],
      );
    } else {
      onChangeSelectedOrders([]);
    }
  }, [selectAllOrders]);

  const status = (() => {
    switch (props.status) {
      case 'created':
        return 'assembly';
      case 'packagedCombined':
        return 'assembly';
      case 'sent':
        return 'sentToRussia';
      case 'received':
        return 'sentToRussia';
    }
  })();

  let filteredItems: Array<{
    method: CleanLocalDeliveryMethod;
    items: CleanTransportation[];
  }> = [];
  if (selectedLocalMethods) {
    const items = allData?.map((data) => data.items).flat(1) || [];

    filteredItems = items.reduce<
      Array<{
        method: CleanLocalDeliveryMethod;
        items: CleanTransportation[];
      }>
    >((acc, order) => {
      const index = acc.findIndex((item) => {
        return order.lines.some(
          (line) =>
            line.deliveryOrder.localDeliveryMethod.id === item.method.id,
        );
      });
      if (index === -1) {
        acc.push({
          method: order.lines[0].deliveryOrder.localDeliveryMethod,
          items: [order],
        });
      } else {
        acc[index].items.push(order);
      }
      return acc;
    }, []);
  }

  return (
    <>
      <div className="pb-4">
        {filteredItems.map((item) => (
          <div className={'border-bottom mb-3'}>
            <DeliveryOrdersTabMethodRow
              method={item.method}
              onToggleMethod={onToggleMethod}
              collapsedMethods={collapsedMethods}
            />
            {!collapsedMethods.includes(item.method.id) &&
              item.items.map((data) => (
                <div ref={data.id === lastItem ? ref : undefined}>
                  <TransportationTabItem
                    key={data.id}
                    data={data}
                    onCollapsedChange={() => {
                      onToggle(data);
                    }}
                    collapsed={
                      (!expanded && collapsed.includes(data.id)) ||
                      (expanded && !collapsed.includes(data.id))
                    }
                    status={status}
                  />
                </div>
              ))}
          </div>
        ))}
      </div>
      {isLoadingMore && <MyLoader />}
    </>
  );
};
